// 🔴 CHANTIER — client HTTP du relevé de travaux (moteur `chantier/`).
//
// 🔑 FRONTIÈRE : ce module ⛔ n'appelle QUE `/api/chantier/*`. ⛔ Aucune route
// `/api/visite/*`, ⛔ aucun composant du DAL, ⛔ rien de `MobileChantierPage` —
// vérifié par `garde_frontiere_artisan.test.js`.
// 🔑 UN transport : la session OmniHub, bureau ET terrain (P-MARIO-03 — ⛔ plus de
//    jeton de visite obtenu par numéro de dossier + code côté écran ; les routes
//    serveur `/api/chantier/mobile/*` restent, ⛔ supprimées sans preuve).
// 🔑 Le tenant ⛔ n'est JAMAIS envoyé : le serveur le lit dans la session ou le jeton.
// ⛔ Aucun prix, ⛔ aucune TVA ne transitent.
const API = process.env.REACT_APP_BACKEND_URL || "";
const e = encodeURIComponent;

/** Erreur HTTP lisible ; `statut` permet à l'écran terrain de reconnaître un 401. */
export class ErreurApi extends Error {
  constructor(message, statut) { super(message); this.statut = statut; }
}

function transport(jeton) {
  const autorisation = () => {
    const j = typeof jeton === "function" ? jeton() : jeton;
    return j ? { Authorization: `Bearer ${j}` } : {};
  };
  const brut = async (chemin, options = {}) => {
    const r = await fetch(`${API}${chemin}`, {
      credentials: "include", ...options, headers: { ...autorisation(), ...(options.headers || {}) },
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      // ⚠️ `detail` est une CHAÎNE côté serveur ; un objet deviendrait « [object Object] ».
      throw new ErreurApi(typeof j?.detail === "string" ? j.detail : `Échec (HTTP ${r.status})`, r.status);
    }
    return r;
  };
  const json = async (chemin, options = {}) => (await brut(chemin, options)).json().catch(() => ({}));
  const envoyer = (chemin, corps, methode = "POST") =>
    json(chemin, { method: methode, headers: { "Content-Type": "application/json" }, body: JSON.stringify(corps) });
  const deposer = (chemin, fichier, posteId) => {
    const f = new FormData();
    f.append("fichier", fichier, fichier.name || "photo.jpg");
    if (posteId) f.append("poste_id", posteId);
    return json(chemin, { method: "POST", body: f });
  };
  const blob = async (chemin) => (await brut(chemin)).blob();
  return { json, envoyer, deposer, blob };
}

/** Actions communes à un relevé, sous une racine donnée. */
function actionsReleve(t, racine) {
  const r = (rid) => `${racine}/${e(rid)}`;
  return {
    lire: (rid) => t.json(r(rid)),
    proposer: (rid, corps) => t.envoyer(`${r(rid)}/postes`, corps),
    // 🔴 N°963 — DICTÉE AUDIO RÉELLE : l'audio du micro MONTE (multipart), le serveur
    //    transcrit (ElevenLabs Scribe) puis passe le TEXTE au MÊME moteur que `proposer`.
    //    ⛔ Aucun secret au front ; ⛔ Content-Type laissé à FormData (frontière multipart).
    dicterAudio: (rid, blob, dureeS) => {
      const f = new FormData();
      f.append("audio", blob, "dictee.webm");
      if (dureeS != null) f.append("duree_s", String(dureeS));
      return t.json(`${r(rid)}/dicter-audio`, { method: "POST", body: f });
    },
    confirmer: (rid, corps) => t.envoyer(`${r(rid)}/postes/confirmer`, corps),
    // 🔴 P-MARIO-04 — retrait d'un poste, et retrait de MASSE en UN appel (tout ou rien) ;
    //    tous deux rendent le relevé relu par le serveur.
    retirer: (rid, pid) => t.json(`${r(rid)}/postes/${e(pid)}`, { method: "DELETE" }),
    retirerPostes: (rid, ids) => t.envoyer(`${r(rid)}/postes/retirer`, { ids }),
    // 🔴 N°915-C — CORRIGER un poste DÉJÀ enregistré (quantité, unité…). La route
    //    `PATCH …/postes/{id}` existait DÉJÀ côté serveur (tenant scopé, relevé clos
    //    REFUSÉ, unité contrôlée contre le profil) : ⛔ aucun second mécanisme, seul
    //    l'accès manquait à l'écran. Rend le relevé RELU par le serveur.
    // ⚠️ `exclude_unset` côté serveur : ⛔ n'envoyer QUE les champs à changer — un
    //    champ absent est CONSERVÉ, un champ à `null` est EFFACÉ (voulu).
    // 🔴 N°919 — fait LIRE un énoncé de quantité par le serveur. ⛔ Ne persiste RIEN.
    //    L'interprétation reste du CÔTÉ CŒUR : la refaire côté téléphone en ferait
    //    une seconde implémentation qui divergerait.
    quantiteDictee: (rid, dictee) => t.envoyer(`${r(rid)}/quantite-dictee`, { dictee }),
    modifier: (rid, pid, champs) => t.envoyer(`${r(rid)}/postes/${e(pid)}`, champs, "PATCH"),
    terminer: (rid) => t.envoyer(`${r(rid)}/terminer`, {}),
    // 🔴 N°1016 — abandonner une visite EN COURS (état durable `abandonne`, ⛔ aucune suppression).
    abandonner: (rid, motif) => t.envoyer(`${r(rid)}/abandonner`, motif ? { motif } : {}),
    lireNote: (rid) => t.json(`${r(rid)}/note`),
    // 🔴 N°894 — `rev` = révision de la note CHARGÉE ; périmée ⇒ 409 (⛔ écrasement silencieux).
    ecrireNote: (rid, texte, rev) => t.envoyer(`${r(rid)}/note`, { texte, rev: rev || 0 }, "PUT"),
    photos: (rid) => t.json(`${r(rid)}/photos`).then((j) => j.photos || []),
    ajouterPhoto: (rid, fichier, posteId) => t.deposer(`${r(rid)}/photos`, fichier, posteId),
    photoBlob: (rid, pid) => t.blob(`${r(rid)}/photos/${e(pid)}`),
    supprimerPhoto: (rid, pid) => t.json(`${r(rid)}/photos/${e(pid)}`, { method: "DELETE" }),
  };
}

function jetonSession() {
  try { return localStorage.getItem("session_token"); } catch { return null; }
}

/** BUREAU — session OmniHub. */
export function apiBureau(dossierId) {
  const t = transport(jetonSession);
  return {
    ...actionsReleve(t, "/api/chantier/sessions"),
    releveEnCours: () => t.json(`/api/chantier/sessions/en-cours?dossier_id=${e(dossierId)}`).then((j) => j.releve || null),
    releves: () => t.json(`/api/chantier/sessions?dossier_id=${e(dossierId)}`).then((j) => j.releves || []),
    // 🔴 N°1016 — le référentiel du tenant (SANS prix ni TVA, filtrés par le serveur) : sert à
    //    affecter une référence à un poste déjà saisi. Lecture seule.
    catalogue: (profilCode) => t.json(`/api/chantier/catalogue?profil_code=${e(profilCode)}`).then((j) => j.produits || []),
    // 🔑 LE mécanisme « Nouvelle visite » — bouton de la fiche dossier ET entrée de l'application
    //    (P-MARIO-15). `contexte` facultatif : libellés client/lieu pour « Reprendre ».
    ouvrir: (profilCode, contexte) => t.envoyer("/api/chantier/sessions",
      { dossier_id: dossierId, profil_code: profilCode, ...(contexte ? { contexte } : {}) }),
  };
}

// ── Compatibilité : les appels du relevé V1 (bureau) ─────────────────────────
export const releveEnCours = (dossierId) => apiBureau(dossierId).releveEnCours();
export const ouvrirReleve = (dossierId, profilCode) => apiBureau(dossierId).ouvrir(profilCode);
export const proposerPoste = (releveId, corps) => apiBureau().proposer(releveId, corps);
export const confirmerPoste = (releveId, corps) => apiBureau().confirmer(releveId, corps);
